import { useRef, useState } from 'react';
import type { CalendarId, ResourceId } from '@bigint-project/core';
import { formatMoney, formatUnits } from './format.js';
import type { ResourceSheetField, ResourceSheetProps, ResourceSheetRow } from './types.js';

/** 純展示元件：props in / callbacks out。零 store、零 Tauri 相依。 */

interface ColumnDef {
  readonly field: ResourceSheetField;
  readonly title: string;
  readonly width: number;
}

const COLUMNS: readonly ColumnDef[] = [
  { field: 'name', title: '名稱', width: 160 },
  { field: 'type', title: '型別', width: 90 },
  { field: 'initials', title: '縮寫', width: 70 },
  { field: 'group', title: '群組', width: 100 },
  { field: 'maxUnits', title: '最大單位', width: 90 },
  { field: 'standardRate', title: 'A 預設標準費率', width: 120 },
  { field: 'overtimeRate', title: 'A 預設加班費率', width: 120 },
  { field: 'costPerUse', title: '每次使用成本', width: 110 },
  { field: 'accrueAt', title: '累算方式', width: 90 },
  { field: 'calendarName', title: '日曆', width: 110 },
];

const BUDGET_NOT_APPLICABLE = new Set<ResourceSheetField>([
  'maxUnits', 'standardRate', 'overtimeRate', 'costPerUse', 'accrueAt', 'calendarName',
]);

function rawValue(row: ResourceSheetRow, field: ResourceSheetField, currency: string): string {
  switch (field) {
    case 'name':
      return row.name;
    case 'type':
      return row.type;
    case 'initials':
      return row.initials;
    case 'group':
      return row.group;
    case 'maxUnits':
      return formatUnits(row.maxUnits as unknown as number);
    case 'standardRate':
      return formatMoney(row.standardRate, currency);
    case 'overtimeRate':
      return formatMoney(row.overtimeRate, currency);
    case 'costPerUse':
      return formatMoney(row.costPerUse as unknown as number, currency);
    case 'accrueAt':
      return row.accrueAt;
    case 'calendarName':
      return row.calendarName;
    default:
      return '';
  }
}

export function ResourceSheet(props: ResourceSheetProps): React.JSX.Element {
  const {
    rows,
    currency,
    selectedResourceIds = [],
    onSelectionChange = () => {},
    onBudgetChange = () => {},
    calendarOptions = [],
    onCalendarChange = () => {},
    onFieldChange,
  } = props;
  const [editing, setEditing] = useState<{ resourceId: ResourceId; field: ResourceSheetField; draft: string } | null>(
    null,
  );
  // 同 Timesheet.tsx 的雙重送出問題：Enter 觸發 commit() 讓 <input> 卸載後，卸載
  // 產生的合成 blur 仍會沿用同一次 render 的 onBlur closure 再呼叫一次 commit()。
  // 用跨 render 存活的 ref 旗標擋掉同一次編輯階段的重複呼叫。
  const committedRef = useRef(false);

  const startEditing = (resourceId: ResourceId, field: ResourceSheetField, draft: string): void => {
    committedRef.current = false;
    setEditing({ resourceId, field, draft });
  };

  const commit = (): void => {
    if (committedRef.current || editing === null) return;
    committedRef.current = true;
    onFieldChange(editing.resourceId, editing.field, editing.draft);
    setEditing(null);
  };

  return (
    <div
      data-testid="resource-sheet"
      className="flex h-full w-full flex-col overflow-auto bg-surface text-sm text-fg"
    >
      <div className="flex border-b border-line-strong bg-raised font-medium">
        <div className="shrink-0 border-r border-line px-2 py-1.5" style={{ width: 42 }}>
          選取
        </div>
        <div className="shrink-0 border-r border-line px-2 py-1.5" style={{ width: 72 }}>
          預算
        </div>
        {COLUMNS.map((column) => (
          <div
            key={column.field}
            role="columnheader"
            className="shrink-0 border-r border-line px-2 py-1.5"
            style={{ width: column.width }}
          >
            {column.title}
          </div>
        ))}
        <div className="shrink-0 border-r border-line px-2 py-1.5" style={{ width: 130 }}>
          基準日曆
        </div>
      </div>

      {rows.map((row) => (
        <div
          key={row.id}
          role="row"
          data-testid={`resource-sheet-row-${row.id}`}
          data-overallocated={row.isOverallocated}
          className={[
            'flex border-b border-hairline',
            row.isOverallocated ? 'bg-red-100 text-red-900 dark:bg-danger-soft dark:text-danger' : '',
          ].join(' ')}
        >
          <div className="flex shrink-0 items-center justify-center border-r border-hairline px-2 py-1" style={{ width: 42 }}>
            <input
              type="checkbox"
              checked={selectedResourceIds.includes(row.id)}
              onChange={(event) => onSelectionChange(row.id, event.target.checked)}
              aria-label={`選取 ${row.name}`}
              data-testid={`resource-sheet-select-${row.id}`}
            />
          </div>
          <div className="flex shrink-0 items-center justify-center border-r border-hairline px-2 py-1" style={{ width: 72 }}>
            <input
              type="checkbox"
              checked={row.isBudget}
              disabled={!row.canSetBudget}
              onChange={(event) => onBudgetChange(row.id, event.target.checked)}
              aria-label={`${row.name} 是預算資源`}
              title={row.canSetBudget ? undefined : '已有一般任務指派，無法改為預算資源'}
            />
          </div>
          {COLUMNS.map((column) => {
            const isEditing = editing?.resourceId === row.id && editing.field === column.field;
            const budgetNotApplicable = row.isBudget && BUDGET_NOT_APPLICABLE.has(column.field);
            if (budgetNotApplicable) {
              return (
                <div
                  key={column.field}
                  role="gridcell"
                  data-testid={`resource-sheet-cell-${row.id}-${column.field}`}
                  className="shrink-0 overflow-hidden border-r border-hairline px-2 py-1 text-fg-muted"
                  style={{ width: column.width }}
                  aria-label={`${column.title}不適用於預算資源`}
                >
                  —
                </div>
              );
            }
            if (column.field === 'calendarName') {
              return (
                <div
                  key={column.field}
                  role="gridcell"
                  data-testid={`resource-sheet-cell-${row.id}-${column.field}`}
                  className="shrink-0 overflow-hidden border-r border-hairline px-2 py-1"
                  style={{ width: column.width }}
                >
                  <select
                    aria-label={`${row.name} 的日曆`}
                    className="w-full bg-transparent outline-none"
                    value={row.calendarId ?? ''}
                    onChange={(event) => onCalendarChange(row.id, event.target.value === '' ? null : (event.target.value as CalendarId))}
                  >
                    <option value="">專案日曆</option>
                    {calendarOptions.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>{calendar.name}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <div
                key={column.field}
                role="gridcell"
                data-testid={`resource-sheet-cell-${row.id}-${column.field}`}
                className="shrink-0 overflow-hidden border-r border-hairline px-2 py-1"
                style={{ width: column.width }}
                onDoubleClick={() => startEditing(row.id, column.field, rawValue(row, column.field, currency))}
              >
                {isEditing ? (
                  <input
                    autoFocus
                    className="w-full bg-transparent outline-none"
                    value={editing.draft}
                    onChange={(event) => setEditing({ ...editing, draft: event.target.value })}
                    onBlur={commit}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') commit();
                      if (event.key === 'Escape') {
                        committedRef.current = true;
                        setEditing(null);
                      }
                    }}
                  />
                ) : (
                  <span className="truncate">{rawValue(row, column.field, currency)}</span>
                )}
              </div>
            );
          })}
          <div className="shrink-0 truncate border-r border-hairline px-2 py-1" style={{ width: 130 }}>
            {row.isBudget ? '—' : row.baseCalendarName}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ResourceSheet;
